import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class OutcomeService {

  private podYearSource = new BehaviorSubject<PODYear>(null);
  currentPODYear$ = this.podYearSource.asObservable();

  private reportYearSource = new BehaviorSubject<PODYear>(null);
  currentReportYear$ = this.reportYearSource.asObservable();

  private requestSource = new BehaviorSubject<OutcomeRequestApi[]>(null);
  currentRequests$ = this.requestSource.asObservable();

  private yearsSource = new BehaviorSubject<number[]>(null);
  currentYears$ = this.yearsSource.asObservable();

  private grantSource = new BehaviorSubject<OutcomeGrantApi[]>(null);
  currentGrants$ = this.grantSource.asObservable();

  private reportSource = new BehaviorSubject<OutcomeReportApi[]>(null);
  currentReports$ = this.reportSource.asObservable();

  private pwapValueSource = new BehaviorSubject<OutcomePwapDiversions[]>(null);
  currentPwapValues$ = this.pwapValueSource.asObservable();

  private eventSource = new BehaviorSubject<OutcomeEventApi[]>(null);
  currentEventsData$ = this.eventSource.asObservable();

  private yearDatesSource = new BehaviorSubject<Date[]>(null);
  currentYearDates$ = this.yearDatesSource.asObservable();

  private ocrequestEventSource = new BehaviorSubject<any>(null);
  currentOCRequestEvent$ = this.ocrequestEventSource.asObservable();

  constructor(
    @Inject('BASE_URL') private baseUrl: string,
    private http: HttpClient,
    private toastrService: ToastrService,
  ) { }


  //Get requests by year
  //it will include all permits' appropriator-by-pod combos
  getYears() {
    this.http.get<number[]>(`${this.baseUrl}api/outcome/searchyears`)
      .subscribe(response => {
      if (!!response) {
        this.changeYears(response);
      } else {
        this.changeYears([]);
      }
    },
      error => console.error(error)
    );
  }

  changeYears(years: number[]) {
    this.yearsSource.next(years);
  }

  //Get requests by year
  //it will include all permits' appropriator-by-pod combos
  getRequests(permitYear: number) {
    this.http.get<OutcomeRequestApi[]>(`${this.baseUrl}api/outcome/search?permitYear=${permitYear}`)
      .subscribe(response => {
      if (!!response) {
        this.changeRequests(response);
      } else {
        this.changeRequests([]);
      }
    },
      error => console.error(error)
    );
  }

  changeRequests(requests: OutcomeRequestApi[]) {
    this.requestSource.next(requests);
  }

  //Get grants by year
  //it will include all permits' appropriator-by-pod combos
  getGrants(permitYear: number) {
    this.http.get<OutcomeGrantApi[]>(`${this.baseUrl}api/outcome/searchgrants?permitYear=${permitYear}`)
      .subscribe(response => {
      if (!!response) {
        this.changeGrants(response);
      } else {
        this.changeGrants([]);
      }
    },
      error => console.error(error)
    );
  }

  changeGrants(grants: OutcomeGrantApi[]) {
    this.grantSource.next(grants);
  }

  getPwapValues(permitYear: number) {
    this.http.get<OutcomePwapDiversions[]>(`${this.baseUrl}api/outcome/searchpwapdiversions?permitYear=${permitYear}`)
      .subscribe(response => {
      if (!!response) {
        this.changePwapValues(response);
      } else {
        this.changePwapValues([]);
      }
    },
      error => console.error(error)
    );
  }

  changePwapValues(pwapValues: OutcomePwapDiversions[]) {
    this.pwapValueSource.next(pwapValues);
  }

  getEventValues(permitYear: number) {
    this.http.get<OutcomeEventApi[]>(`${this.baseUrl}api/outcome/SearchEvents?PermitYear=${permitYear}`)
      .subscribe(response => {
    if (!!response) {
      this.changeEventValues(response);
    } else {
      this.changeEventValues([]);
    }
    },
      error => console.error(error)
    );
  }

  changeEventValues(newValues: OutcomeEventApi[]) {
    this.eventSource.next(newValues);
  }

  //this can handle both creation and subsequent update of a request
  putRequest(request: OutcomeRequestApi) {
    var year = request.year;
    this.http.post<any>(`${this.baseUrl}api/outcome`, request).subscribe(
      response => {
        if (response) {
          this.getRequests(year);
          //this.changeRequests(response);
          this.toastrService.success("Outcome request updated.");
        }
      },
      error => {
        console.error(error);
        this.toastrService.success("Outcome request update failed.");
      }
    );
  }

  changePODYear(year: PODYear) {
    this.podYearSource.next(year);
  }

  changeReportYear(year: PODYear) {
    this.reportYearSource.next(year);
  }

  changeOCRequestEvent($event) {
    this.ocrequestEventSource.next($event);
  }

  getReports(permitYear: number) {
    this.http.get<OutcomeReportApi[]>(`${this.baseUrl}api/outcome/searchreports?permitYear=${permitYear}`)
      .subscribe(response => {
      if (!!response) {
        this.changeReports(response);
      } else {
        this.changeReports([]);
      }
    },
      error => console.error(error)
    );
  }

  changeReports(reports: OutcomeReportApi[]) {
    this.reportSource.next(reports);
  }

  changeYearDates(dates: Date[]) {
    this.yearDatesSource.next(dates);
  }

  leapYear(year) {
    return ((year % 4 == 0) && (year % 100 != 0)) || (year % 400 == 0);
  }
}

export interface PODYear {
  year: number;
}

export interface Outcome {
  dates: Date[],
  requests: number[],
  actuals: number[],
  grants: number[],
  applicationNumbers: string[],
  waterRightsIds: string[],
  permitName: string,
  project: string,
  appropriatorName: string,
  permitPointOfDiversionName: string,
  appropriatorId: number,
  permitAnnualOperatingPlanId: number,
  permitPointOfDiversionId: number
}

export interface OutcomeRequestApi {
  outcomeRequestId : number
  permitAnnualOperatingPlanId : number
  requestCFS : number
  startDate : Date
  endDate : Date
  notes : string

  applicationNumber : string,
  waterRightsId : string
  actual : number
  grant : number
  appropriatorId : number
  appropriatorName : string
  permitPointOfDiversionId : number
  permitPointOfDiversionName : string
  year : number
}

export interface OutcomeGrantApi {
  grant : number
  permitStartDate : Date
  permitEndDate : Date
  applicationPackageId : number
  applicationNumber : string
  appropriatorId : number
  appropriatorName : string
  permitPointOfDiversionId : number
  permitPointOfDiversionName : string
  year : number
  waterRightsId: string
}

export interface OutcomeEventApi {
  allocated: number;
  startDate: Date;
  endDate: Date;
  permitAnnualOperatingPlanId: number;
  applicationPackageId: number;
  applicationNumber: string;
  appropriatorId: number;
  appropriatorName: string;
  permitPointOfDiversionId: number;
  permitPointOfDiversionName: string;
  externalNoticeId: string;
  year: number;
}

export interface OutcomeReportApi {
  permitPointOfDiversionName : string
  waterRightsId : string
  applicationNumber : string
  grant : string
  startDate : string
  endDate : string
  requests: OutcomeReportRequestApi[]
}

export interface OutcomeReportRequestApi {
  requestCFS: number
  startDate: string
  endDate: string
  applicationNumber : string
  waterRightsId : string
}

export interface OutcomePwapDiversions {
  permitPointOfDiversionId: number
  permitPointOfDiversionName: string
  pwapValues: PwapValueApi[]
}

export interface PwapValueApi {
  pwapReportDate: Date
  value: number
}
